import { useState, useEffect, useCallback } from 'react';

interface ResponsiveColumnsConfig {
  default: number;
  sm?: number;  // 640px
  md?: number;  // 768px
  lg?: number;  // 1024px
  xl?: number;  // 1280px
}

/**
 * Hook for calculating responsive column counts based on viewport width
 * Matches Tailwind CSS breakpoints
 */
export const useResponsiveColumns = (config: ResponsiveColumnsConfig): number => {
  const [columns, setColumns] = useState(config.default);

  const calculateColumns = useCallback(() => {
    if (globalThis.window === undefined) return config.default;

    const width = globalThis.window.innerWidth;

    if (config.xl && width >= 1280) {
      return config.xl;
    }
    if (config.lg && width >= 1024) {
      return config.lg;
    }
    if (config.md && width >= 768) {
      return config.md;
    }
    if (config.sm && width >= 640) {
      return config.sm;
    }
    return config.default;
  }, [config]);

  useEffect(() => {
    const handleResize = () => {
      setColumns(calculateColumns());
    };

    // Initial calculation
    handleResize();

    globalThis.window.addEventListener('resize', handleResize, { passive: true });
    return () => globalThis.window.removeEventListener('resize', handleResize);
  }, [calculateColumns]);

  return columns;
};

/**
 * Predefined column configurations for different content types
 */
export const VIDEO_GRID_COLUMNS: ResponsiveColumnsConfig = {
  default: 1,
  sm: 2,
  lg: 3,
};

export const IMAGE_GRID_COLUMNS: ResponsiveColumnsConfig = {
  default: 1,
  sm: 2,
  lg: 3,
  xl: 4,
};

export const TRANSCRIPTION_GRID_COLUMNS: ResponsiveColumnsConfig = {
  default: 1,
  sm: 2,
  lg: 3,
};

export default useResponsiveColumns;
